'use client';

import * as SliderPrimitive from '@radix-ui/react-slider';
import * as React from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    showTooltip?: boolean;
    tooltipContent?: (value: number) => React.ReactNode;
    /** Accessible label forwarded to each interactive thumb. */
    thumbAriaLabel?: string;
    /** Override the default (light-surface) track/range/thumb colors — e.g. for a scrubber laid over video. */
    trackClassName?: string;
    rangeClassName?: string;
    thumbClassName?: string;
  }
>(
  (
    {
      className,
      showTooltip = false,
      tooltipContent,
      thumbAriaLabel,
      trackClassName,
      rangeClassName,
      thumbClassName,
      ...props
    },
    ref,
  ) => {
    const [showTooltipState, setShowTooltipState] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState<number[]>(
      (props.defaultValue as number[]) ?? (props.value as number[]) ?? [0],
    );

    React.useEffect(() => {
      if (props.value !== undefined) {
        setInternalValue(props.value as number[]);
      }
    }, [props.value]);

    const handleValueChange = (newValue: number[]) => {
      setInternalValue(newValue);
      props.onValueChange?.(newValue);
    };

    const handlePointerDown = () => {
      if (showTooltip) {
        setShowTooltipState(true);
      }
    };

    const handlePointerUp = React.useCallback(() => {
      if (showTooltip) {
        setShowTooltipState(false);
      }
    }, [showTooltip]);

    React.useEffect(() => {
      if (showTooltip) {
        document.addEventListener('pointerup', handlePointerUp);
        return () => {
          document.removeEventListener('pointerup', handlePointerUp);
        };
      }
    }, [showTooltip, handlePointerUp]);

    const renderThumb = (value: number) => {
      const thumb = (
        <SliderPrimitive.Thumb
          data-slot="thumb"
          aria-label={thumbAriaLabel}
          className={cn(
            'block h-4 w-4 rounded-full border border-border bg-background shadow-sm transition hover:scale-110 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-ring/40 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
            thumbClassName,
          )}
          onPointerDown={handlePointerDown}
        />
      );

      if (!showTooltip) return thumb;

      return (
        <TooltipProvider>
          <Tooltip open={showTooltipState}>
            <TooltipTrigger asChild>{thumb}</TooltipTrigger>
            <TooltipContent
              className="px-2 py-1 text-xs font-medium"
              sideOffset={8}
              side={props.orientation === 'vertical' ? 'right' : 'top'}
            >
              <p>{tooltipContent ? tooltipContent(value) : value}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    };

    return (
      <SliderPrimitive.Root
        ref={ref}
        data-slot="slider"
        className={cn(
          'relative flex w-full touch-none select-none items-center data-[orientation=vertical]:h-full data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col data-[disabled]:opacity-50',
          className,
        )}
        onValueChange={handleValueChange}
        {...props}
      >
        <SliderPrimitive.Track
          data-slot="track"
          className={cn(
            'relative grow overflow-hidden rounded-full bg-accent/30 data-[orientation=horizontal]:h-1.5 data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-1.5',
            trackClassName,
          )}
        >
          <SliderPrimitive.Range
            data-slot="range"
            className={cn(
              'absolute bg-foreground/60 data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
              rangeClassName,
            )}
          />
        </SliderPrimitive.Track>
        {internalValue?.map((value, index) => (
          <React.Fragment key={index}>{renderThumb(value)}</React.Fragment>
        ))}
      </SliderPrimitive.Root>
    );
  },
);
Slider.displayName = 'Slider';

export { Slider };
